import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Lock, ArrowRight, Loader2, CheckCircle2, XCircle, Eye, EyeOff } from "lucide-react";
import { Logo } from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setAuth } = useAuth();
  const token = searchParams.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password || password.length < 6) {
      toast.error("Passwort muss mindestens 6 Zeichen haben");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwörter stimmen nicht überein");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/auth/set-password`, { token, password });
      if (res.data.success) {
        setAuth(res.data.token, res.data.user);
        setDone(true);
        toast.success("Passwort erfolgreich gesetzt!");
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || "Fehler beim Setzen des Passworts";
      const isCors = !err.response && err.message?.includes("Network");
      const msg = isCors
        ? "Verbindungsfehler – bitte öffnen Sie den Link über Ihre Domain (webgenerator.io) statt der Vercel-Preview-URL."
        : detail;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-8 max-w-md w-full text-center">
          <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-5">
            <XCircle className="w-7 h-7 text-red-500" />
          </div>
          <h1 className="text-xl font-bold text-slate-900 mb-2" data-testid="invalid-token">Ungültiger Link</h1>
          <p className="text-slate-500 text-sm mb-6">Dieser Link ist ungültig oder wurde bereits verwendet.</p>
          <Button onClick={() => navigate("/")} className="bg-indigo-600 hover:bg-indigo-700">
            Zur Startseite
          </Button>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-8 max-w-md w-full text-center">
          <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-5">
            <CheckCircle2 className="w-7 h-7 text-green-500" />
          </div>
          <h1 className="text-xl font-bold text-slate-900 mb-2" data-testid="password-set-success">Passwort gesetzt!</h1>
          <p className="text-slate-500 text-sm mb-6">
            Sie können sich jetzt jederzeit mit Ihrer E-Mail und Ihrem Passwort anmelden.
          </p>
          <Button
            data-testid="goto-dashboard-btn"
            onClick={() => navigate("/meine-seiten")}
            className="w-full bg-indigo-600 hover:bg-indigo-700"
          >
            Zum Dashboard <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <Logo size="lg" clickable={false} />
          </Link>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-8">
          <div className="mb-6">
            <h1 className="text-xl font-bold text-slate-900 mb-1" data-testid="set-password-title">
              Passwort festlegen
            </h1>
            <p className="text-sm text-slate-500">
              Legen Sie ein sicheres Passwort fest, um auf Ihr Dashboard zuzugreifen.
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-5 text-sm text-red-700" data-testid="set-password-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="password" className="text-sm font-medium text-slate-700 mb-1.5 block">
                Neues Passwort
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  data-testid="new-password"
                  id="password"
                  type={showPw ? "text" : "password"}
                  placeholder="Mindestens 6 Zeichen"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 h-11 rounded-xl"
                />
                <button type="button" data-testid="toggle-pw" onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="confirm" className="text-sm font-medium text-slate-700 mb-1.5 block">
                Passwort bestätigen
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  data-testid="confirm-password"
                  id="confirm"
                  type={showConfirm ? "text" : "password"}
                  placeholder="Passwort wiederholen"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10 pr-10 h-11 rounded-xl"
                />
                <button type="button" data-testid="toggle-confirm" onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
                  {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button
              data-testid="set-password-submit"
              type="submit"
              disabled={loading}
              className="w-full h-11 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Passwort festlegen <ArrowRight className="w-4 h-4 ml-1.5" /></>}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
